import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ValuePropositionSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activePhase, setActivePhase] = useState(0);

  const phases = [
    {
      title: "Build",
  subtitle: "Create with Purpose",
  icon: "Wrench",
  color: "from-primary to-success",
  description: "We encourage learners to start by creating something tangible — a project, prototype, or product — turning ideas into action from day one.",
  features: [
    "Turn concepts into real projects",
    "Focus on learning by doing",
    "Encourage creativity and experimentation",
    "Develop problem-solving skills through making"
  ],
      image: "/assets/images/build1.png",
      outcome: "Learners gain hands-on experience by turning concepts into tangible projects, building confidence and technical skills from the start."
    },
    {
      title: "Break",
  subtitle: "Test & Push Limits",
  icon: "Hammer",
  color: "from-destructive to-warning",
  description: "We break down the walls between theoretical education and practical industry needs. No more outdated curricula or irrelevant assignments.",
  features: [
    "Identify skill gaps in current education",
    "Remove theoretical-only learning barriers",
    "Challenge outdated teaching methods",
    "Break free from generic degree programs"
  ],
      image: "/assets/images/break.png",
      outcome: "Students learn critical thinking and problem-spotting by testing their creations, identifying flaws, and challenging traditional approaches."
    },
    {
      title: "Understand",
  subtitle: "Learn from Experience",
  icon: "BookOpen",
  color: "from-secondary to-primary",
  description: "Learners analyze outcomes, reflect on mistakes, and extract deep insights to understand both successes and failures.",
  features: [
    "Analyze what worked and what didn’t",
    "Learn by reflecting on experiments",
    "Strengthen fundamentals through practice",
    "Develop resilience through iteration"
  ],
      image: "/assets/images/understand.png",
      outcome: "Learners develop a deep understanding of fundamentals by analyzing mistakes, reflecting on outcomes, and strengthening problem-solving skills."
    },
    {
      title: "Innovate",
  subtitle: "Create Impact",
  icon: "Sparkles",
  color: "from-success to-accent",
  description: "The final step is innovation — applying insights to build impactful, real-world solutions that drive change and inspire progress.",
  features: [
    "Transform knowledge into solutions",
    "Encourage entrepreneurial thinking",
    "Focus on real-world impact",
    "Foster continuous improvement and innovation"
  ],
      image: "/assets/images/innovate.png",
      outcome: "Students evolve into innovators and solution-creators, capable of delivering real-world, industry-ready solutions with long-lasting impact."
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById('value-proposition');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

/*  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActivePhase((prev) => (prev + 1) % phases.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);*/

  return (
    <section id="value-proposition" className="py-20 bg-gradient-to-b from-background to-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            Our <span className="text-nipix-blue">Build-Break-Understand-Innovate</span> Methodology
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            A proven 4-phase approach that transforms students from theoretical learners to industry-ready professionals with guaranteed career outcomes.
          </p>
        </motion.div>

        {/* Interactive Timeline */}
        <div className="relative">
          {/* Phase Navigation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            {phases.map((phase, index) => (
              <button
                key={index}
                onClick={() => setActivePhase(index)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-full transition-all duration-300 ${
                  activePhase === index
                    ? 'bg-primary text-white shadow-lg scale-105'
                    : 'bg-white text-text-secondary hover:bg-muted border border-border'
                }`}
              >
                <Icon 
                  name={phase.icon} 
                  size={20} 
                  strokeWidth={2}
                  className={activePhase === index ? 'text-white' : 'text-primary'}
                />
                <span className="font-semibold">{phase.title}</span>
              </button>
            ))}
          </motion.div>

          {/* Active Phase Content */}
          <motion.div
            key={activePhase}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-3xl shadow-xl border border-border overflow-hidden"
          >
            <div className="grid lg:grid-cols-2 gap-0">
              {/* Content Side */}
              <div className="p-8 lg:p-12">
                <div className="mb-6">
  <div className="inline-flex items-center space-x-3 px-4 py-2 rounded-full bg-white text-nipix-blue border border-nipix-blue mb-4">
    <Icon 
      name={phases[activePhase].icon} 
      size={24} 
      strokeWidth={2}
      className="text-nipix-blue"
    />
    <span className="font-bold text-lg text-nipix-blue">
      {phases[activePhase].title}
    </span>
  </div>
  <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
    {phases[activePhase].subtitle}
  </h3>
  <p className="text-lg text-text-secondary leading-relaxed">
    {phases[activePhase].description}
  </p>
</div>


                {/* Features List */}
                <div className="space-y-4 mb-8">
                  {phases[activePhase].features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      className="flex items-center space-x-3"
                    >
                      <div className="w-6 h-6 bg-success/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name="Check" size={14} className="text-success" strokeWidth={2} />
                      </div>
                      <span className="text-text-secondary">{feature}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Outcome Badge */}
                <div className="bg-gradient-to-r from-success/10 to-primary/10 rounded-xl p-4 border border-success/20">
                  <div className="flex items-start space-x-3">
                    <Icon name="Target" size={20} className="text-success mt-1" strokeWidth={2} />
                    <div>
                      <div className="font-semibold text-text-primary mb-1">Expected Outcome</div>
                      <div className="text-sm text-text-secondary">{phases[activePhase].outcome}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Visual Side */}
              <div className="relative bg-gradient-to-br from-muted to-surface p-8 lg:p-12 flex items-center justify-center">
                <div className="relative w-full max-w-md">
                  <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                    <Image
                      src={phases[activePhase].image}
                      alt={`${phases[activePhase].title} Phase`}
                      className="w-full h-64 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-4 text-white">
                      <div className="text-sm font-medium">Phase {activePhase + 1}</div>
                      <div className="text-lg font-bold">{phases[activePhase].title}</div>
                    </div>
                  </div>

                  {/* Floating Success Indicators 
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="absolute -top-4 -right-4 bg-white rounded-xl p-3 shadow-lg border border-border"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="TrendingUp" size={16} className="text-success" strokeWidth={2} />
                      <span className="text-xs font-semibold text-text-primary">Success Rate: 94%</span>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="absolute -bottom-4 -left-4 bg-white rounded-xl p-3 shadow-lg border border-border"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name="Clock" size={16} className="text-primary" strokeWidth={2} />
                      <span className="text-xs font-semibold text-text-primary">Avg. Duration: 3-6 months</span>
                    </div>
                  </motion.div>*/}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Progress Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isVisible ? 1 : 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex justify-center mt-8 space-x-2"
          >
            {phases.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === activePhase 
                    ? 'w-8 bg-primary' 
                    : index < activePhase 
                      ? 'w-4 bg-success' :'w-4 bg-border'
                }`}
              />
            ))}
          </motion.div>
        </div>

        {/* Call to Action 
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Career?</h3>
            <p className="text-lg opacity-90 mb-6 max-w-2xl mx-auto">
              Join thousands of students who have successfully bridged the gap between academic learning and industry success through our proven methodology.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  const element = document.getElementById('apply-section');
                  if (element) {
                    const headerHeight = 80;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                  }
                }}
                className="bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-300"
              >
                Start Your Journey Today
              </button>
              <button
                onClick={() => {
                  const element = document.getElementById('courses');
                  if (element) {
                    const headerHeight = 80;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                  }
                }}
                className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-primary transition-all duration-300"
              >
                Explore Our Courses
              </button>
            </div>
          </div>
        </motion.div>*/}
      </div>
    </section>
  );
};

export default ValuePropositionSection;